import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { BooleanPropertyDTO, IngredientDocStockDTO } from 'business/models/Models';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import RMTable from 'components/customComponents/Controls/RMTable';
import { PropertyConstants } from 'enums/PropertyConstants';
import React, { useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import {
  useHasBufferType,
  useHasNonBufferType,
  useIngredientInfo,
  useIngredientStocks,
  useSelectedStockRows,
  useStocksErrors,
} from 'redux/hooks/IngredientDetail';
import {
  addStock,
  removeStocks,
  setDefaultBufferStock,
  setDefaultNonBufferStock,
  setMergedAndDeletedStockIds,
  setSelectedStockRows,
  updateStockById,
  updateStockPropertyById,
} from 'redux/reducers/ingredientDetailReducer';
import { IStyle } from 'Styles';
import {
  useFetchConcentrationUnits,
  useGetAllLiquidClasses,
  useGetAllVendors,
  useGetStockDefaultDispenseOrder,
  useGetStocksTableData,
} from './hooks';
import { getNewDefaultStock, getVisibleStockColumns } from './utils/utils';
import './stocks.css';
import { MergeStocks } from 'components/ingredients/mergeStocks/MergeStocks';
import RMCustomButton, {
  RMCustomButtonProps,
} from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { RMModal, useModalHandlers } from 'components/modal';

const Stocks = () => {
  const stocks = useIngredientStocks();
  const ingInfo = useIngredientInfo();
  const dispatch = useRMDispatch();
  const vendors = useGetAllVendors();
  const { liquidClasses, defaultLiquidClassId } = useGetAllLiquidClasses();
  const selectedRows = useSelectedStockRows();
  const hasBufferType = useHasBufferType();
  const hasNonBufferType = useHasNonBufferType();
  const hideIrrelevantColumns = useSystemProperty<BooleanPropertyDTO>(
    PropertyConstants.HideIrrelevantColumns,
    false,
    { refreshOnUpdate: true },
  );
  const stockColumns = getVisibleStockColumns(
    hideIrrelevantColumns,
    hasBufferType,
    hasNonBufferType,
  );
  const stockErrorInfo = useStocksErrors();
  const concUnits = useFetchConcentrationUnits();
  const [showModal, setShowModal] = useState<boolean>(false);
  const defaultDispenseOrder = useGetStockDefaultDispenseOrder();
  const [defaultNonBufferStockId, setDefaultNonBufferStockId] = useState<number>();
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const { showModal: showErrorModal, hideModal: hideErrorModal } = useModalHandlers();

  const onCellChanged = (e, stockId) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    switch (e.target.name) {
      case 'nonBufferDefault':
        if (value) {
          setShowWarning(true);
          setDefaultNonBufferStockId(stockId);
        }
        break;
      case 'bufferDefault':
        dispatch(setDefaultBufferStock(value ? stockId : null));
        break;
      default:
        dispatch(
          updateStockPropertyById({ id: stockId, value: value, key: e.target.name }),
        );
        break;
    }
  };

  const stockRows = useGetStocksTableData({
    onCellChanged,
    vendors,
    liquidClasses,
    ingInfo,
    concUnits,
  });

  const handleAddStock = () => {
    dispatch(
      addStock(getNewDefaultStock(stocks, defaultLiquidClassId, defaultDispenseOrder)),
    );
  };

  const handleDeleteStocks = () => {
    if (selectedRows?.length) {
      if (selectedRows.some((i) => stocks[i].isUsed)) {
        showErrorModal({
          heading: 'Error',
          body: 'You cannot delete this stock because it is in use.',
          onOk: hideErrorModal,
          isOkOnly: true,
          okButtonText: 'Ok',
        });
      } else {
        dispatch(
          removeStocks(
            selectedRows.map((i) => {
              return stocks[i];
            }),
          ),
        );
      }
    }
  };

  const handleMergeStocks = () => {
    if (selectedRows?.length === 2) {
      const stockOne = stocks[selectedRows[0]];
      const stockTwo = stocks[selectedRows[1]];
      if (
        stockOne?.stockConcentration !== stockTwo?.stockConcentration ||
        stockOne?.stockPh !== stockTwo?.stockPh ||
        stockOne?.concentrationUnits !== stockTwo?.concentrationUnits
      ) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Stocks must have the same concentration, concentration unit, and pH.',
        );
        return;
      }
      setShowModal(true);
    }
  };

  const addStockIcon: RMCustomButtonProps = {
    Icon: <RMSvgIcon name="New" className="w-8 h-8 cursor-pointer" />,
    titleTip: 'Add Stocks',
    onClick: handleAddStock,
  };

  const deleteStockIcon: RMCustomButtonProps = {
    Icon: (
      <RMSvgIcon subfolder="Setup" name="Remove" className="w-8 h-8 cursor-pointer" />
    ),
    titleTip: 'Delete Stocks',
    onClick: handleDeleteStocks,
  };

  const mergeStockIcon: RMCustomButtonProps = {
    Icon: (
      <RMSvgIcon
        subfolder="Setup"
        name="MergeStocks"
        className="w-8 h-8 cursor-pointer"
      />
    ),
    titleTip: 'Merge Stocks',
    onClick: handleMergeStocks,
    isDisabled: selectedRows?.length !== 2,
  };

  const handleSelectionChange = (rowIndices) => {
    dispatch(setSelectedStockRows([...rowIndices]));
  };

  const onClickMergeStock = (mergeStockObj, id) => {
    setShowModal(false);
    const mergeStock = mergeStockObj.mergeStock;
    const vendor = vendors.find((vendor) => vendor.id === mergeStock.vendorId);
    mergeStock.vendor = vendor;
    mergeStock.vendorId = vendor?.id;
    const tempMergedAndDeletedStockIds = { key: id, value: mergeStock.id };
    dispatch(setMergedAndDeletedStockIds(tempMergedAndDeletedStockIds));
    dispatch(updateStockById(mergeStock));
    dispatch(removeStocks(stocks.filter((stock) => stock.id === id)));
    dispatch(setSelectedStockRows([]));
  };
  return (
    <div
      id="stocks-main-container"
      className="flex basis-full flex-col max-h-full dashed-border"
    >
      <div
        id="stocks-title"
        className={`p-2 font-bold ${
          hasBufferType ? 'basis-[13%] max-h-[13%]' : 'basis-1/12'
        }`}
      >
        Stocks
      </div>
      <div
        id="stocks-table-wrapper"
        className={`${
          hasBufferType ? 'basis-[73%] max-h-[71%]' : 'basis-10/12 max-h-[83.3%]'
        } flex`}
      >
        <RMTable
          data={stockRows}
          columns={stockColumns}
          selectedIds={selectedRows}
          onCheckboxSelectionChange={handleSelectionChange}
          mainContainerStyle={styles.tableStyle}
          rowExtraInfo={stockErrorInfo}
          selectRowOnClick
        />
      </div>
      <div
        id="stocks-buttons-wrapper"
        className={`${
          hasBufferType ? 'basis-[15%]' : 'basis-1/12'
        } flex gap-2 px-2 pb-1 items-end`}
      >
        <RMCustomButton {...addStockIcon} />
        <RMCustomButton {...deleteStockIcon} />
        <RMCustomButton {...mergeStockIcon} />
      </div>
      <MergeStocks
        ingredientName={ingInfo.longName}
        stockOne={stocks[selectedRows[0]] as IngredientDocStockDTO}
        stockTwo={stocks[selectedRows[1]] as IngredientDocStockDTO}
        showModal={showModal}
        setStocksShowModal={setShowModal}
        onClickMergeStock={onClickMergeStock}
      ></MergeStocks>
      <RMModal
        id="stocks-modal-warning"
        showModal={showWarning}
        setShowModal={setShowWarning}
        onOk={() => {
          setShowWarning(false);
          dispatch(setDefaultNonBufferStock(defaultNonBufferStockId));
        }}
        body={'Warning: You are about to change the default non-buffer stock.'}
        heading={'Change Non-Buffer Default'}
      />
    </div>
  );
};

const styles: IStyle = {
  tableStyle: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
};

export default Stocks;
